import { inject, Injectable } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { UserService } from '../services/user.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageGuard {
  private validLanguages: string[] = ['pt', 'en'];

  constructor(private translate: TranslateService, private us: UserService) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree | Observable<boolean | UrlTree>> {
    const browserLang = this.getBrowserLanguage();
    const defaultLang = browserLang || 'en';

    // Define o idioma padrão (fallback)
    this.translate.setDefaultLang(defaultLang);

    // Tenta usar o idioma do usuário logado
    let userLang: string | null = null;
    try {
      const cu = await this.us.getCurrentUser();
      userLang = cu?.language?.toLowerCase() ?? null;
    } catch (error) {
      console.error(error);
    }

    // Prioridade: idioma do usuário > idioma do navegador > 'en'
    const langToUse = (userLang && this.validLanguages.includes(userLang))
      ? userLang
      : defaultLang;

    this.translate.use(langToUse);

    return true;
  }

  changeLanguage(lang: string) {
    if (this.validLanguages.includes(lang)) {
      this.translate.use(lang);
    }
  }

  private getBrowserLanguage(): string | null {
    const browserLanguage = navigator.language?.split('-')[0];
    const browserLanguages = navigator.languages?.map(
      (lang) => lang.split('-')[0]
    );

    if (browserLanguage && this.validLanguages.includes(browserLanguage)) {
      return browserLanguage;
    }

    if (browserLanguages) {
      for (const lang of browserLanguages) {
        if (this.validLanguages.includes(lang)) {
          return lang;
        }
      }
    }

    return null;
  }
}
